import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const HTMLPerformance = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Performance & Optimization</div>
                <h1>HTML Performance Optimization</h1>
                <p className="hero-lead">
                    Build lightning-fast websites with HTML optimization techniques. Learn about resource hints,
                    lazy loading, code splitting, and Core Web Vitals for optimal user experience.
                </p>
            </div>

            <div className="content-section">
                <h2>Why Performance Matters</h2>
                <p>
                    Fast websites provide better user experience, higher engagement, better SEO rankings,
                    and increased conversions. Even a 1-second delay can significantly impact user satisfaction.
                </p>

                <div className="stats-grid">
                    <div className="stat-card">
                        <h3>53%</h3>
                        <p>of mobile users abandon sites that take over 3 seconds to load</p>
                    </div>
                    <div className="stat-card">
                        <h3>100ms</h3>
                        <p>faster = 1% increase in conversions (Amazon study)</p>
                    </div>
                    <div className="stat-card">
                        <h3>2x</h3>
                        <p>better ranking potential with fast Core Web Vitals</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Core Web Vitals</h2>
                <p>
                    Google's metrics for measuring user experience:
                </p>

                <div className="vitals-grid">
                    <div className="vital-card">
                        <h3>LCP - Largest Contentful Paint</h3>
                        <p><strong>Measures:</strong> Loading performance</p>
                        <p><strong>Goal:</strong> &lt; 2.5 seconds</p>
                        <p><strong>What it is:</strong> Time until largest content element is visible</p>
                    </div>

                    <div className="vital-card">
                        <h3>FID - First Input Delay</h3>
                        <p><strong>Measures:</strong> Interactivity</p>
                        <p><strong>Goal:</strong> &lt; 100 milliseconds</p>
                        <p><strong>What it is:</strong> Time from first interaction to browser response</p>
                    </div>

                    <div className="vital-card">
                        <h3>CLS - Cumulative Layout Shift</h3>
                        <p><strong>Measures:</strong> Visual stability</p>
                        <p><strong>Goal:</strong> &lt; 0.1</p>
                        <p><strong>What it is:</strong> How much content moves during page load</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Resource Hints</h2>
                <p>
                    Tell the browser what resources to prioritize:
                </p>

                <h3>1. DNS Prefetch</h3>
                <p>Resolve DNS early for external domains:</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<head>
    <!-- Resolve DNS for external resources --&gt;
    <link rel="dns-prefetch" href="https://fonts.googleapis.com">
    <link rel="dns-prefetch" href="https://cdn.example.com">
    <link rel="dns-prefetch" href="https://analytics.google.com">
</head>`} />
                </div>

                <h3>2. Preconnect</h3>
                <p>Establish early connection (DNS + TCP + TLS):</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<head>
    <!-- Full connection for critical resources --&gt;
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
</head>`} />
                </div>

                <h3>3. Prefetch</h3>
                <p>Download resources for future navigation:</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<head>
    <!-- Fetch resources user might need next --&gt;
    <link rel="prefetch" href="/next-page.html">
    <link rel="prefetch" href="/images/hero-next.jpg">
    <link rel="prefetch" as="script" href="/analytics.js">
</head>`} />
                </div>

                <h3>4. Preload</h3>
                <p>High-priority resources needed for current page:</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<head>
    <!-- Critical resources for current page --&gt;
    <link rel="preload" href="/fonts/main.woff2" as="font" type="font/woff2" crossorigin>
    <link rel="preload" href="/css/critical.css" as="style">
    <link rel="preload" href="/images/hero.jpg" as="image">
    <link rel="preload" href="/js/main.js" as="script">
</head>`} />
                </div>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Hint</th>
                                <th>When</th>
                                <th>What It Does</th>
                                <th>Use Case</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>dns-prefetch</code></td>
                                <td>Future navigation</td>
                                <td>Resolves DNS only</td>
                                <td>External domains</td>
                            </tr>
                            <tr>
                                <td><code>preconnect</code></td>
                                <td>Current/soon</td>
                                <td>DNS + TCP + TLS</td>
                                <td>Critical external resources</td>
                            </tr>
                            <tr>
                                <td><code>prefetch</code></td>
                                <td>Future navigation</td>
                                <td>Downloads resource</td>
                                <td>Next page resources</td>
                            </tr>
                            <tr>
                                <td><code>preload</code></td>
                                <td>Current page</td>
                                <td>Downloads with priority</td>
                                <td>Critical current resources</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>Lazy Loading</h2>

                <h3>Native Image Lazy Loading</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Lazy load images below the fold --&gt;
<img src="image1.jpg" alt="Above fold" loading="eager">
<img src="image2.jpg" alt="Below fold" loading="lazy">
<img src="image3.jpg" alt="Below fold" loading="lazy">

<!-- Also works for iframes --&gt;
<iframe src="https://youtube.com/embed/..." loading="lazy"></iframe>`} />
                </div>

                <h3>Lazy Loading with Intersection Observer</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<img data-src="image.jpg" class="lazy" alt="Lazy loaded image">

<script>
const lazyImages = document.querySelectorAll('img.lazy');

const imageObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
        if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            img.classList.remove('lazy');
            observer.unobserve(img);
        }
    });
}, {
    rootMargin: '50px' // Start loading 50px before visible
});

lazyImages.forEach(img => imageObserver.observe(img));
</script>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Minimizing Layout Shift (CLS)</h2>

                <h3>Reserve Space for Images</h3>
                <div className="code-example">
                    <div className="code-header">
                        <span> Bad: No Size Specified</span>
                    </div>
                    <CodeBlock language="html" code={`<img src="photo.jpg" alt="Photo">
<!-- Image loads, page jumps! --&gt;`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Good: Dimensions Specified</span>
                    </div>
                    <CodeBlock language="html" code={`<img src="photo.jpg" alt="Photo" width="800" height="600">
<!-- Browser reserves space, no jump --&gt;`} />
                </div>

                <h3>Aspect Ratio for Responsive Images</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<style>
img {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 9; /* Reserve space even before load */
}
</style>

<img src="photo.jpg" alt="Photo" width="1600" height="900">`} />
                </div>

                <h3>Reserve Space for Ads/Embeds</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<div class="ad-container" style="min-height: 250px;">
    <!-- Ad loads here --&gt;
</div>

<div class="video-container" style="aspect-ratio: 16/9;">
    <iframe src="..." loading="lazy"></iframe>
</div>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Critical CSS</h2>
                <p>
                    Inline critical CSS for above-the-fold content:
                </p>

                <div className="code-example">
                    <CodeBlock language="html" code={`<head>
    <!-- Inline critical CSS (above-the-fold styles) --&gt;
    <style>
        /* Critical styles for initial render */
        body { margin: 0; font-family: Arial; }
        header { background: #333; color: white; padding: 1rem; }
        .hero { min-height: 400px; }
    </style>
    
    <!-- Defer non-critical CSS --&gt;
    <link rel="preload" href="/css/main.css" as="style" 
          onload="this.onload=null;this.rel='stylesheet'">
    <noscript><link rel="stylesheet" href="/css/main.css"></noscript>
</head>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Script Loading Strategies</h2>

                <h3>Default (Blocking)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<script src="script.js"></script>
<!-- Blocks HTML parsing until script loads and executes --&gt;`} />
                </div>

                <h3>Async</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<script src="analytics.js" async></script>
<!-- Downloads in parallel, executes when ready (may block parsing) --&gt;
<!-- Good for: independent scripts (analytics, ads) --&gt;`} />
                </div>

                <h3>Defer</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<script src="main.js" defer></script>
<!-- Downloads in parallel, executes after HTML parsing --&gt;
<!-- Good for: scripts that need DOM ready --&gt;`} />
                </div>

                <div className="script-comparison">
                    <table>
                        <thead>
                            <tr>
                                <th>Attribute</th>
                                <th>Download</th>
                                <th>Execute</th>
                                <th>Use For</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>None (default)</td>
                                <td>Blocks parsing</td>
                                <td>Immediately</td>
                                <td>Critical scripts</td>
                            </tr>
                            <tr>
                                <td><code>async</code></td>
                                <td>Parallel</td>
                                <td>When ready</td>
                                <td>Analytics, ads</td>
                            </tr>
                            <tr>
                                <td><code>defer</code></td>
                                <td>Parallel</td>
                                <td>After parsing</td>
                                <td>DOM-dependent scripts</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h3>Module Scripts</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Deferred by default --&gt;
<script type="module" src="app.js"></script>

<!-- Async module --&gt;
<script type="module" src="widget.js" async></script>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Optimizing Images</h2>

                <h3>1. Use Correct Format</h3>
                <div className="format-guide">
                    <ul>
                        <li><strong>WebP/AVIF:</strong> Best compression, modern browsers</li>
                        <li><strong>JPEG:</strong> Photos, universal support</li>
                        <li><strong>PNG:</strong> Graphics with transparency</li>
                        <li><strong>SVG:</strong> Logos, icons (scalable)</li>
                    </ul>
                </div>

                <h3>2. Responsive Images</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<picture>
    <source type="image/webp" srcset="image.webp">
    <source type="image/jpeg" srcset="image.jpg">
    <img src="image.jpg" alt="..." width="800" height="600" loading="lazy">
</picture>

<!-- Or with srcset --&gt;
<img src="image-800.jpg"
     srcset="image-400.jpg 400w,
             image-800.jpg 800w,
             image-1200.jpg 1200w"
     sizes="(max-width: 600px) 400px, 800px"
     alt="..."
     width="800"
     height="600"
     loading="lazy">`} />
                </div>

                <h3>3. Compression</h3>
                <ul>
                    <li>Use tools like TinyPNG, ImageOptim, Squoosh</li>
                    <li>Serve appropriately sized images (don't serve 4000px for 400px display)</li>
                    <li>Use CDN with automatic optimization</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Reduce HTTP Requests</h2>

                <h3>1. Inline Small Resources</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Inline small SVGs --&gt;
<svg width="24" height="24">...</svg>

<!-- Data URLs for tiny images --&gt;
<img src="data:image/svg+xml,..." alt="Icon">

<!-- Inline critical CSS --&gt;
<style>/* Critical CSS */</style>`} />
                </div>

                <h3>2. CSS Sprites (for icons)</h3>
                <div className="code-example">
                    <CodeBlock language="css" code={`<style>
.icon {
    background-image: url('sprites.png');
    width: 24px;
    height: 24px;
}
.icon-home { background-position: 0 0; }
.icon-user { background-position: -24px 0; }
.icon-search { background-position: -48px 0; }
</style>`} />
                </div>

                <h3>3. Combine Files</h3>
                <ul>
                    <li>Bundle JavaScript files</li>
                    <li>Concatenate CSS files</li>
                    <li>Use build tools (Webpack, Vite, Parcel)</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Minimize Render-Blocking Resources</h2>

                <div className="code-example">
                    <div className="code-header">
                        <span>Optimized Head Section</span>
                    </div>
                    <CodeBlock language="html" code={`<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Page Title</title>
    
    <!-- Preconnect to external domains --&gt;
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    
    <!-- Inline critical CSS --&gt;
    <style>/* Above-the-fold styles */</style>
    
    <!-- Preload critical resources --&gt;
    <link rel="preload" href="/fonts/main.woff2" as="font" type="font/woff2" crossorigin>
    
    <!-- Async load non-critical CSS --&gt;
    <link rel="preload" href="/css/main.css" as="style" 
          onload="this.onload=null;this.rel='stylesheet'">
    <noscript><link rel="stylesheet" href="/css/main.css"></noscript>
    
    <!-- Defer JavaScript --&gt;
    <script src="/js/main.js" defer></script>
</head>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Font Loading Optimization</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<head>
    <!-- Preconnect to font CDN --&gt;
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    
    <!-- Preload fonts --&gt;
    <link rel="preload" href="/fonts/main.woff2" as="font" type="font/woff2" crossorigin>
    
    <!-- Load fonts with display swap --&gt;
    <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap" 
          rel="stylesheet">
</head>

<style>
/* Use font-display to prevent FOIT (Flash of Invisible Text) */
@font-face {
    font-family: 'CustomFont';
    src: url('/fonts/custom.woff2') format('woff2');
    font-display: swap; /* Show fallback font while loading */
}
</style>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Performance Monitoring</h2>

                <h3>Lighthouse (Chrome DevTools)</h3>
                <ul>
                    <li>Open Chrome DevTools (F12)</li>
                    <li>Go to Lighthouse tab</li>
                    <li>Run audit for Performance</li>
                    <li>Follow recommendations</li>
                </ul>

                <h3>Web Vitals Measurement</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Use web-vitals library --&gt;
<script type="module">
import {onCLS, onFID, onLCP} from 'https://unpkg.com/web-vitals@3?module';

onCLS(console.log);
onFID(console.log);
onLCP(console.log);
</script>`} />
                </div>

                <h3>Performance API</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`<script>
// Measure page load time
window.addEventListener('load', () => {
    const perfData = performance.timing;
    const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
    const connectTime = perfData.responseEnd - perfData.requestStart;
    const renderTime = perfData.domComplete - perfData.domLoading;
    
    console.log('Page Load Time:', pageLoadTime + 'ms');
    console.log('Connect Time:', connectTime + 'ms');
    console.log('Render Time:', renderTime + 'ms');
});

// Resource timing
performance.getEntriesByType('resource').forEach(resource => {
    console.log(resource.name, resource.duration);
});
</script>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Complete Optimized Template</h2>
                <div className="code-example">
                    <div className="code-header">
                        <span>Performance-Optimized HTML Template</span>
                    </div>
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta name="description" content="Page description for SEO">
    <title>Page Title</title>
    
    <!-- Resource hints --&gt;
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="dns-prefetch" href="https://analytics.google.com">
    
    <!-- Critical CSS inline --&gt;
    <style>
        /* Above-the-fold critical styles */
        body { margin: 0; font-family: system-ui; }
        .hero { min-height: 400px; }
    </style>
    
    <!-- Preload critical resources --&gt;
    <link rel="preload" href="/fonts/main.woff2" as="font" type="font/woff2" crossorigin>
    <link rel="preload" href="/images/hero.webp" as="image">
    
    <!-- Async CSS --&gt;
    <link rel="preload" href="/css/main.css" as="style" 
          onload="this.onload=null;this.rel='stylesheet'">
    <noscript><link rel="stylesheet" href="/css/main.css"></noscript>
    
    <!-- Deferred JavaScript --&gt;
    <script src="/js/main.js" defer></script>
</head>
<body>
    <!-- Above-the-fold content --&gt;
    <header>
        <h1>Page Title</h1>
        <nav>
            <a href="/">Home</a>
            <a href="/about">About</a>
        </nav>
    </header>
    
    <main>
        <!-- Hero with explicit dimensions --&gt;
        <section class="hero">
            <picture>
                <source type="image/webp" srcset="hero.webp">
                <img src="hero.jpg" alt="Hero" width="1200" height="600" loading="eager">
            </picture>
        </section>
        
        <!-- Below-the-fold content with lazy loading --&gt;
        <section>
            <img src="image1.jpg" alt="..." width="800" height="600" loading="lazy">
            <img src="image2.jpg" alt="..." width="800" height="600" loading="lazy">
        </section>
    </main>
    
    <footer>
        <p>&copy; 2024 Company Name</p>
    </footer>
</body>
</html>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Performance Checklist</h2>
                <div className="checklist">
                    <h3>HTML Optimization:</h3>
                    <ul>
                        <li> Minify HTML in production</li>
                        <li> Use semantic HTML</li>
                        <li> Minimize DOM depth</li>
                        <li> Remove unused code</li>
                    </ul>

                    <h3>Images:</h3>
                    <ul>
                        <li> Use modern formats (WebP, AVIF)</li>
                        <li> Specify width and height</li>
                        <li> Lazy load below-the-fold images</li>
                        <li> Use responsive images (srcset)</li>
                        <li> Compress images</li>
                    </ul>

                    <h3>CSS:</h3>
                    <ul>
                        <li> Inline critical CSS</li>
                        <li> Async load non-critical CSS</li>
                        <li> Minify CSS</li>
                        <li> Remove unused CSS</li>
                    </ul>

                    <h3>JavaScript:</h3>
                    <ul>
                        <li> Use defer or async attributes</li>
                        <li> Minify JavaScript</li>
                        <li> Code splitting</li>
                        <li> Tree shaking</li>
                    </ul>

                    <h3>Fonts:</h3>
                    <ul>
                        <li> Preload critical fonts</li>
                        <li> Use font-display: swap</li>
                        <li> Subset fonts</li>
                        <li> Use WOFF2 format</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices Summary</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Specify image dimensions</li>
                            <li>Use resource hints appropriately</li>
                            <li>Lazy load below-the-fold content</li>
                            <li>Defer non-critical JavaScript</li>
                            <li>Inline critical CSS</li>
                            <li>Optimize images and use modern formats</li>
                            <li>Monitor Core Web Vitals</li>
                            <li>Test on real devices and connections</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Block rendering with synchronous scripts</li>
                            <li>Forget image dimensions (causes CLS)</li>
                            <li>Load all images eagerly</li>
                            <li>Use render-blocking CSS</li>
                            <li>Serve unoptimized images</li>
                            <li>Ignore performance metrics</li>
                            <li>Test only on fast connections</li>
                            <li>Assume performance is "good enough"</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HTMLPerformance;




